import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, api, clearTokens, getAccess, setTokens } from "@/lib/api";
import { clientKind, deviceFingerprint, deviceName } from "@/lib/deviceIdentity";
import type { Me, TokenPair } from "@/types/types";

interface AuthCtx {
  user: Me | null;
  loading: boolean;
  login: (username: string, password: string, captchaToken?: string) => Promise<void>;
  register: (username: string, password: string, invite?: string, email?: string, captchaToken?: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthCtx | null>(null);

// last known profile, so the app still opens with no network (downloaded
// music has to play in the metro) instead of bouncing to the login page
const ME_KEY = "sl.me";
function rememberMe(me: Me) { try { localStorage.setItem(ME_KEY, JSON.stringify(me)); } catch { /* ignore */ } }

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    if (!getAccess()) { setLoading(false); return; }
    try {
      const me = await api<Me>("/api/auth/me");
      setUser(me);
      rememberMe(me);
    } catch (e) {
      // only a real "no" from the server ends the session; a network error
      // or a server outage keeps it and runs on the cached profile
      if (e instanceof ApiError && e.status === 401) {
        clearTokens();
        try { localStorage.removeItem(ME_KEY); } catch { /* ignore */ }
      } else {
        try {
          const cached = localStorage.getItem(ME_KEY);
          if (cached) setUser(JSON.parse(cached) as Me);
        } catch { /* ignore */ }
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { void bootstrap(); }, [bootstrap]);

  const login = useCallback(async (username: string, password: string, captchaToken?: string) => {
    const p = await api<TokenPair>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({
        username, password,
        device_fingerprint: deviceFingerprint(),
        device_name: deviceName(),
        client: clientKind(),
        captcha_token: captchaToken,
      }),
    });
    setTokens(p);
    const me = await api<Me>("/api/auth/me");
    setUser(me);
    rememberMe(me);
  }, []);

  const register = useCallback(async (username: string, password: string, invite?: string, email?: string, captchaToken?: string) => {
    await api<Me>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({
        username, password, invite_code: invite, email,
        captcha_token: captchaToken,
      }),
    });
    await login(username, password);
  }, [login]);

  const logout = useCallback(async () => {
    const rt = localStorage.getItem("sl.refresh");
    if (rt) { try { await api("/api/auth/logout", { method: "POST", body: JSON.stringify({ refresh_token: rt }) }); } catch {} }
    clearTokens();
    try { localStorage.removeItem(ME_KEY); } catch { /* ignore */ }
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, register, logout }), [user, loading, login, register, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
